import { useQuery, useMutation } from "@tanstack/react-query";
import { ipInventoryApiService } from "modules/Pharmacy/IPinventory/api/ipInventory.api";
import { IpInventoryFormValues } from "modules/Pharmacy/IPinventory/helpers/ipInventory.types";


interface ApiResponse<T = any> {
  data: T;
  message: string;
  statuscode: number;
}

export const useIpInventoryService = ({
  searchText,
  pageNumber = 1,
  pageSize,
  enableSelectall = false,
  studyid,
  ipid,
  receiptnumber,
  ipcategory,
}: {
  searchText?: string | null;
  pageNumber?: number;
  pageSize?: number;
  studyid?: number | string | null;
  enableSelectall?: boolean;
  ipid?: number | string | null;
  receiptnumber?: string | null;
  ipcategory?: string | null;
} = {}) => {

  // SELECT ALL
  const SelectAllQuery = useQuery({
    queryKey: [
      "ipinventory-selectAll",
      { searchText, pageNumber, pageSize, studyid },
    ],
    queryFn: () =>
      ipInventoryApiService.getSelectAllData({
        searchtext: searchText ?? null,
        currentpage: pageNumber,
        pagesize: pageSize,
        status: "A",
        studyid: studyid ?? null,
      }),
    enabled: enableSelectall,
  });

  //Delete
  const DeleteMutation = useMutation<ApiResponse, Error, { ipid: number | null, receiptnumber: string; studyid: number; updatereason: string }>({
    mutationFn: (payload) =>
      ipInventoryApiService.deleteData({ ipid: payload.ipid, receiptnumber: payload.receiptnumber, studyid: payload.studyid, updatereason: payload.updatereason }),
  });

  // INSERT
  const InsertMutation = useMutation({
    mutationFn: (payload: IpInventoryFormValues) => ipInventoryApiService.insertData(payload),
  });

  // UPDATE
  const UpdateMutation = useMutation({
    mutationFn: (payload: IpInventoryFormValues & { ipid: number | string; updatereason: string; deletedAttachmentIds?: number[] }) => ipInventoryApiService.updateData(payload),
  });

  // SELECT BY ID
  const SelectQuery = (studyid: number | string | null) =>
    useQuery({
      queryKey: ["ipinventory-select-study", studyid],  // ← unique key
      queryFn: () => ipInventoryApiService.getSelectData(studyid!),
      enabled: !!studyid,
    });

  // SELECT BY IPID OR STUDYID & IPCATEGORY (for view/edit/insert prefill)
  const SelectIdQuery = useQuery({
    queryKey: [
      "ipinventory-select-ip",
      ipid ? Number(ipid) : null,
      studyid ? Number(studyid) : null,
      receiptnumber ? String(receiptnumber) : null,
      ipcategory ? String(ipcategory) : null,
    ],
    queryFn: () =>
      ipInventoryApiService.getSelectIpDetailData(
        ipid
          ? {
              ipid: Number(ipid),
              studyid: Number(studyid),
              receiptnumber: String(receiptnumber),
            }
          : {
              studyid: Number(studyid),
              ipcategory: String(ipcategory),
            }
      ),
    enabled: (!!ipid && !!studyid && !!receiptnumber) || (!ipid && !!studyid && !!ipcategory),
  });

  const SelectIpIDQuery = (studyid: number | string | null) =>
    useQuery({
      queryKey: ["ipinventory-select-studyid", studyid],         // ← unique key
      queryFn: () => ipInventoryApiService.getSelectIpData(studyid!),
      enabled: !!studyid,
    });

  const verifyPinMutation = useMutation({
    mutationFn: (payload: { verifypin: string; ipid?: number | string | null; receiptnumber?: string | null; studyid?: number | string | null; verifiedby: number }) => ipInventoryApiService.verifyPin(payload),
  });

  // INSERT ACCEPTANCE
  const InsertAcceptanceMutation = useMutation({
    mutationFn: (payload: any) => ipInventoryApiService.insertAcceptanceData(payload),
  });

  // UPDATE ACCEPTANCE
  const UpdateAcceptanceMutation = useMutation({
    mutationFn: (payload: any) => ipInventoryApiService.updateAcceptanceData(payload),
  });

  return { SelectAllQuery, InsertMutation, UpdateMutation, SelectQuery, DeleteMutation, SelectIdQuery, SelectIpIDQuery, verifyPinMutation, InsertAcceptanceMutation, UpdateAcceptanceMutation };
};